import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCanvas } from "../../../../context/CanvasContext";
import ConfirmationModal from "../../../../../shared/components/modals/ConfirmationModal";
import EditModal from "../../../../../shared/components/modals/EditModal";
import { FiCheck, FiDollarSign } from "react-icons/fi";
import { toast } from "react-toastify";
import InstructionsModal from "../../../../../shared/components/InstructionsModal";
import FileUploadButton from "../../../../../shared/components/FileUploadButton";
import {
  fetchExternalSubscriptions,
  createExternalSubscription,
  updateExternalSubscription,
  deleteExternalSubscription,
  uploadExternalSubscriptions,
  selectExternalSubscriptions,
  selectExternalSubscriptionLoading,
  selectExternalSubscriptionPagination,
  subscriptionOverview,
  selectSubscriptionOverview,
} from "../../../../../../app/store/slices/externalSubscriptionSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import Pagination from '../../../../../shared/components/Pagination';
import { PAGE_SIZE_OPTIONS, SUB_FREQUENCY_OPTIONS, subscriptionTableColumns } from '../../../../../../utils/constants/dashboardConstants';
import TableHeader from '../../../../../shared/components/TableHeader';
import ActionButtons from '../../../../../shared/components/ActionButton';
import FormInput from '../../../../../shared/ui/FormInput';
import FormSelect from '../../../../../shared/ui/FormSelect';
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import { formatDateDMY } from '../../../../../../utils/formatDate';
import TableBody from '../../../../../shared/components/TableBody';
import SummaryCard from "../../../../../shared/components/SummaryCard";
import { CATEGORIES } from "../../../../../../utils/constants/dashboardConstants";
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import { FaMoneyCheckAlt } from "react-icons/fa";
import {formatAmount} from '../../../../../../utils/formatAmount';




const SubscriptionContent = () => {
  const dispatch = useDispatch();
  const { selectedCanvas } = useCanvas();
  const subscriptions = useSelector(selectExternalSubscriptions);
  const loading = useSelector(selectExternalSubscriptionLoading);
  const pagination = useSelector(selectExternalSubscriptionPagination);
  const accounts = useSelector(selectAccounts);
  const overview = useSelector(selectSubscriptionOverview);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);

  // Data states
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editSubscriptionData, setEditSubscriptionData] = useState({
    itemName: "",
    category: "",
    frequency: "",
    subscriptionCost: "",
    subscriptionDate: "",
    subscriptionRenewalDate: "",
    paymentAccountId: "",
  });
  const [addSubscriptionData, setAddSubscriptionData] = useState({
    itemName: "",
    category: "",
    frequency: "",
    subscriptionCost: "",
    subscriptionDate: "",
    subscriptionRenewalDate: "",
    paymentAccountId: "",
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const fileInputRef = useRef();
  const addOptionsRef = useRef(null);
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  const [showInstructions, setShowInstructions] = useState(false);
  const formatCurrency = (amount) => {
    return `${selectedCanvas?.currency} ${formatAmount(amount)}`;
  };
 

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchExternalSubscriptions({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchAccounts({ canvasId: selectedCanvas.id, limit:1000 }));
      dispatch(subscriptionOverview({ canvasId: selectedCanvas.id })); 
      
    }
  }, [dispatch, selectedCanvas?.id, currentPage, pageSize]);

  // Autofill edit form
  useEffect(() => {
    if (showEditForm && selectedSubscription) {
      setEditSubscriptionData({
        itemName: selectedSubscription.itemName || "",
        category: selectedSubscription.category || "",
        frequency: selectedSubscription.frequency || "",
        subscriptionCost: selectedSubscription.subscriptionCost ?? "",
        subscriptionDate: selectedSubscription.subscriptionDate ? selectedSubscription.subscriptionDate.slice(0, 10) : "",
        subscriptionRenewalDate: selectedSubscription.subscriptionRenewalDate ? selectedSubscription.subscriptionRenewalDate.slice(0, 10) : "",
        paymentAccountId: selectedSubscription.paymentAccountId || "",
      });
    }
  }, [showEditForm, selectedSubscription]);

  // Handle Delete
  const handleDeleteSubscription = async () => {
    if (!selectedSubscription || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Subscription");
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(
        deleteExternalSubscription({
          canvasId: selectedCanvas.id,
          subscriptionId: selectedSubscription._id,
        })
      ).unwrap();
      setShowDeleteConfirm(false);
      setSelectedSubscription(null);
      dispatch(fetchExternalSubscriptions({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(subscriptionOverview({ canvasId: selectedCanvas.id })); 
      toast.success("Subscription Deleted Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Edit
  const handleEditSubscription = async (e) => {
    e.preventDefault();
    if (!selectedSubscription || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Subscription");
      return;
    }
    setIsEditing(true);
    try {
      await dispatch(
        updateExternalSubscription({
          canvasId: selectedCanvas.id,
          subscriptionId: selectedSubscription._id,
          updateData: {
            ...editSubscriptionData,
            subscriptionCost: Number(editSubscriptionData.subscriptionCost),
          },
        })
      ).unwrap();
      setShowEditForm(false);
      setSelectedSubscription(null);
      dispatch(fetchExternalSubscriptions({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(subscriptionOverview({ canvasId: selectedCanvas.id })); 
      toast.success("Subscription Updated Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle Add
  const handleAddSubscription = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) {
      toast.error("Invalid Canvas");
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(
        createExternalSubscription({
          canvasId: selectedCanvas.id,
          subscriptionData: {
            ...addSubscriptionData,
            subscriptionCost: Number(addSubscriptionData.subscriptionCost),
          },
        })
      ).unwrap();
      setShowAddForm(false);
      setAddSubscriptionData({
        itemName: "",
        category: "",
        frequency: "",
        subscriptionCost: "",
        subscriptionDate: "",
        subscriptionRenewalDate: "",
        paymentAccountId: "",
      });
      dispatch(fetchExternalSubscriptions({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(subscriptionOverview({ canvasId: selectedCanvas.id })); 
      toast.success("Subscription Created Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCanvas?.id) {
      toast.error("Invalid Canvas ID or file");
      return;
    }
    try {
      await dispatch(uploadExternalSubscriptions({ canvasId: selectedCanvas.id, file })).unwrap();
      dispatch(fetchExternalSubscriptions({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      toast.success("Subscriptions Imported Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  // Handle Download Template for Subscriptions
  const handleDownloadTemplate = () => {
    try {
      const sampleAccountName = accounts && accounts.length > 0 ? accounts[0].name : 'Subscription Account';
      const templateData = [
        {
          itemName: 'Netflix',
          category: 'Entertainment',
          frequency: 'Monthly',
          subscriptionCost: '15',
          subscriptionDate: '2025-01-01',
          subscriptionRenewalDate: '2025-02-01',
          paymentAccountId: sampleAccountName,
        },
        {
          itemName: 'Spotify',
          category: 'Music',
          frequency: 'Monthly',
          subscriptionCost: '10',
          subscriptionDate: '2025-01-05',
          subscriptionRenewalDate: '2025-02-05',
          paymentAccountId: sampleAccountName,
        },
        {
          itemName: 'Amazon Prime',
          category: 'Shopping',
          frequency: 'Yearly',
          subscriptionCost: '120',
          subscriptionDate: '2025-01-10',
          subscriptionRenewalDate: '2026-01-10',
          paymentAccountId: sampleAccountName,
        }
      ];

      const csvContent = [
        'itemName,category,frequency,subscriptionCost,subscriptionDate,subscriptionRenewalDate,paymentAccountId',
        ...templateData.map(row =>
          `${row.itemName},${row.category},${row.frequency},${row.subscriptionCost},${row.subscriptionDate},${row.subscriptionRenewalDate},${row.paymentAccountId}`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscriptions_template.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setShowInstructions(false)
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  const handleCategoryChange = (value, isEdit = false) => {
    if (isEdit) {
      setEditSubscriptionData({ ...editSubscriptionData, category: value });
    } else {
      setAddSubscriptionData({ ...addSubscriptionData, category: value });
    }

    if (value.length > 0) {
      const filtered = CATEGORIES.filter(cat =>
        cat.toLowerCase().includes(value.toLowerCase())
      );
      setCategorySuggestions(filtered);
      setShowCategorySuggestions(true);
    } else {
      setShowCategorySuggestions(false);
    }
  };

  const selectCategory = (category, isEdit = false) => {
    if (isEdit) {
      setEditSubscriptionData({ ...editSubscriptionData, category });
    } else {
      setAddSubscriptionData({ ...addSubscriptionData, category });
    }
    setShowCategorySuggestions(false);
  };

  const calculateRenewalDate = (subscriptionDate, frequency) => {
    if (!subscriptionDate || !frequency) return '';
    
    const startDate = new Date(subscriptionDate);
    let renewalDate = new Date(startDate);
    
    switch (frequency.toLowerCase()) {
      case 'daily':
        renewalDate.setDate(startDate.getDate() + 1);
        break;
      case 'weekly':
        renewalDate.setDate(startDate.getDate() + 7);
        break;
      case 'bi-weekly':
      case 'biweekly':
        renewalDate.setDate(startDate.getDate() + 14);
        break;
      case 'monthly':
        renewalDate.setMonth(startDate.getMonth() + 1);
        break;
      case 'quarterly':
        renewalDate.setMonth(startDate.getMonth() + 3);
        break;
      case 'semi-annual':
      case 'semi-annually':
        renewalDate.setMonth(startDate.getMonth() + 6);
        break;
      case 'yearly':
      case 'annual':
      case 'annually':
        renewalDate.setFullYear(startDate.getFullYear() + 1);
        break;
      default:
        return '';
    }
    
    return renewalDate.toISOString().split('T')[0];
  };

  const handleSubscriptionDateChange = (date, isEdit = false) => {
    const frequency = isEdit ? editSubscriptionData.frequency : addSubscriptionData.frequency;
    const renewalDate = calculateRenewalDate(date, frequency);
    
    if (isEdit) {
      setEditSubscriptionData({
        ...editSubscriptionData,
        subscriptionDate: date,
        subscriptionRenewalDate: renewalDate
      });
    } else {
      setAddSubscriptionData({
        ...addSubscriptionData,
        subscriptionDate: date,
        subscriptionRenewalDate: renewalDate
      });
    }
  };

  const handleFrequencyChange = (frequency, isEdit = false) => {
    const subscriptionDate = isEdit ? editSubscriptionData.subscriptionDate : addSubscriptionData.subscriptionDate;
    const renewalDate = calculateRenewalDate(subscriptionDate, frequency);
    
    if (isEdit) {
      setEditSubscriptionData({
        ...editSubscriptionData,
        frequency: frequency,
        subscriptionRenewalDate: renewalDate
      });
    } else {
      setAddSubscriptionData({
        ...addSubscriptionData,
        frequency: frequency,
        subscriptionRenewalDate: renewalDate
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6">Subscriptions</h1>
     <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
      {/* Header Section*/}
      <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6 overflow-visible">
        {/* Overview Cards */}
        <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto">
          {/**total Subscription */} 
         <SummaryCard
            title="Total Subscriptions"
            value = {overview?.totalSubscriptions !== undefined ? overview.totalSubscriptions : "Auto calculated"}
            icon={<FaMoneyCheckAlt className="w-5 h-5" />}
            valueColor="text-btn-primary"
            iconBg="bg-blue-100"
            iconColor="text-btn-primary"
            formatValue={val => val}
          />
           <SummaryCard
            title="Monthly Spend"
            value =  {overview?.totalMonthlyCost !== undefined ? formatCurrency(overview.totalMonthlyCost) : "Auto calculated"}
            icon={<FiCheck className='w-5 h-5'/>}
            valueColor="text-green-700"
            iconBg="bg-green-100"
            iconColor="text-green-600"
            formatValue={val => val}
          />
          
        </div>
        {/* Add Subscription Button */}
        <AddOptionsButton
            showOptions={showAddOptions}
            setShowOptions={setShowAddOptions}
            addOptionsRef={addOptionsRef}
            onAddManual={() => setShowAddForm(true)}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            onShowInstructions={() => setShowInstructions(true)}
            buttonLabel="Add Subscription"
            buttonTitle="Add Subscription"
            manualLabel="Add Manually"
            FileUploadButtonComponent={FileUploadButton}
          />
      </div>
      {/* Table Container  */}
      
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <TableHeader columns={subscriptionTableColumns} />
                <TableBody
                  loading={loading}
                  data={subscriptions}
                  columnsCount={9}
                  emptyMessage="No Subscriptions Found."
                  loadingMessage="Loading Subscriptions..."
                  renderRow={sub => (
                      <tr key={sub?._id}>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{sub.itemName}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{sub.category}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{sub.frequency}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatAmount(sub.subscriptionCost)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateDMY(sub?.subscriptionDate)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateDMY(sub?.subscriptionRenewalDate)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {accounts.find(a => a._id === sub.paymentAccountId)?.name || "—"}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          <ActionButtons
                            onEdit={() => {
                              setSelectedSubscription(sub);
                              setShowEditForm(true);
                            }}
                            onDelete={() => {
                              setSelectedSubscription(sub);
                              setShowDeleteConfirm(true);
                            }}
                          />
                        </td>
                      </tr>
                  )}
                />
              </table>
              {/* Pagination Controls */}
              <Pagination
                pagination={pagination}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                PAGE_SIZE_OPTIONS={PAGE_SIZE_OPTIONS}
                label="Subscriptions per page"
                totalLabel="total Subscriptions"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Subscription"
        message={`Are you sure you want to delete "${selectedSubscription?.itemName}"? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedSubscription(null);
        }}
        onConfirm={handleDeleteSubscription}
        loading={isDeleting}
      />
      {/* Edit Modal */}
      <EditModal
        show={showEditForm}
        title="Edit Subscription"
        onCancel={() => {
          setShowEditForm(false);
          setSelectedSubscription(null);
        }}
        onSubmit={handleEditSubscription}
        loading={isEditing}
        submitLabel="Save"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Item"
            type="text"
            value={editSubscriptionData.itemName}
            onChange={e => setEditSubscriptionData({ ...editSubscriptionData, itemName: e.target.value })}
            required
          />
          <FormInput
            label="Category"
            type="text"
            value={editSubscriptionData.category}
            onChange={e => handleCategoryChange(e.target.value, true)}
            required
          />
          {showCategorySuggestions && editSubscriptionData.category && (
            <div className="absolute z-10 bg-white border rounded shadow-md mt-1 max-h-60 overflow-auto w-full">
              {categorySuggestions.length > 0 ? (
                categorySuggestions.map((cat, idx) => (
                  <div
                    key={idx}
                    className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
                    onClick={() => selectCategory(cat, true)}
                  >
                    {cat}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">No suggestions found</div>
              )}
            </div>
          )}
          <FormSelect
            label="Frequency"
            value={editSubscriptionData.frequency}
            onChange={e => setEditSubscriptionData({ ...editSubscriptionData, frequency: e.target.value })}
            required
          >
            <option value="">Select Frequency</option>
            {SUB_FREQUENCY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Amount"
            type="number"
            value={editSubscriptionData.subscriptionCost}
            onChange={e => setEditSubscriptionData({ ...editSubscriptionData, subscriptionCost: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Subscription Date"
            type="date"
            value={editSubscriptionData.subscriptionDate}
            onChange={e => handleSubscriptionDateChange(e.target.value, true)}
            required
          />
          <FormInput
            label="Renewal Date"
            type="date"
            value={editSubscriptionData.subscriptionRenewalDate}
            onChange={e => setEditSubscriptionData({ ...editSubscriptionData, subscriptionRenewalDate: e.target.value })}
            required
          />
          <FormSelect
            label="Account"
            value={editSubscriptionData.paymentAccountId}
            onChange={e => setEditSubscriptionData({ ...editSubscriptionData, paymentAccountId: e.target.value })}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
        </div>
      </EditModal>
    
      {/*  Subscription Modal */}
      <EditModal
        show={showAddForm}
        title="Add Subscription"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleAddSubscription}
        loading={isAdding}
        submitLabel="Add"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Item"
            type="text"
            value={addSubscriptionData.itemName}
            onChange={e => setAddSubscriptionData({ ...addSubscriptionData, itemName: e.target.value })}
            required
          />
          <FormInput
            label="Category"
            type="text"
            value={addSubscriptionData.category}
            onChange={e => handleCategoryChange(e.target.value)}
            required
          />
          {showCategorySuggestions && addSubscriptionData.category && (
            <div className="absolute z-10 bg-white border rounded shadow-md mt-1 max-h-60 overflow-auto w-full">
              {categorySuggestions.length > 0 ? (
                categorySuggestions.map((cat, idx) => (
                  <div
                    key={idx}
                    className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
                    onClick={() => selectCategory(cat)}
                  >
                    {cat}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">No suggestions found</div>
              )}
            </div>
          )}
          <FormSelect
            label="Frequency"
            value={addSubscriptionData.frequency}
            onChange={e => handleFrequencyChange(e.target.value, false)}
            required
          >
            <option value="">Select Frequency</option>
            {SUB_FREQUENCY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Amount"
            type="number"
            value={addSubscriptionData.subscriptionCost}
            onChange={e => setAddSubscriptionData({ ...addSubscriptionData, subscriptionCost: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Subscription Date"
            type="date"
            value={addSubscriptionData.subscriptionDate}
            onChange={e => handleSubscriptionDateChange(e.target.value, false)}
            required
          />
          <FormInput
            label="Renewal Date"
            type="date"
            value={addSubscriptionData.subscriptionRenewalDate}
            onChange={e => setAddSubscriptionData({ ...addSubscriptionData, subscriptionRenewalDate: e.target.value })}
            required
          />
          <FormSelect
            label="Account"
            value={addSubscriptionData.paymentAccountId}
            onChange={e => setAddSubscriptionData({ ...addSubscriptionData, paymentAccountId: e.target.value })}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
        </div>
      </EditModal>

    <InstructionsModal
            show={showInstructions}
            onClose={() => setShowInstructions(false)}
            type="subscription"
            onDownloadTemplate={handleDownloadTemplate}
          />
        </div>
  );
};

export default SubscriptionContent;